/**
 * Supabase Auth with Discord OAuth Provider
 *
 * This module provides secure authentication using Supabase's built-in
 * Discord OAuth integration. Users get proper JWT tokens and full RLS protection.
 */

import { supabase } from './supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { AppUser } from './types';

// Re-export for backward compatibility
export type { AppUser };

export interface AuthResult {
  user: User;
  session: Session;
  appUser: AppUser;
}

/**
 * Initialize Supabase Auth session
 * Checks for existing session or redirects to Discord OAuth
 */
export async function authenticateWithSupabase(): Promise<AuthResult> {
  if (import.meta.env.DEV) console.log('[Supabase Auth] Checking for existing session...');

  // Check for existing session
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) {
    console.error('[Supabase Auth] Error getting session:', sessionError);
    throw new Error('Failed to get authentication session');
  }

  // If we have a valid session, fetch user data
  if (session?.user) {
    if (import.meta.env.DEV)
      console.log(
        '[Supabase Auth] Found existing session for:',
        session.user.email || session.user.id
      );

    try {
      const appUser = await fetchOrCreateAppUser(session.user);
      return {
        user: session.user,
        session,
        appUser,
      };
    } catch (error) {
      console.error('[Supabase Auth] Error fetching user data:', error);
      throw new Error('Failed to load user profile');
    }
  }

  // No session - need to authenticate
  if (import.meta.env.DEV) console.log('[Supabase Auth] No session found');
  // Do NOT auto-redirect. Let the UI handle the unauthenticated state (LoginScreen).
  throw new Error('No session found');
}

/**
 * Sign in with Discord OAuth via Supabase Auth
 */
export async function signInWithDiscord(): Promise<void> {
  if (import.meta.env.DEV) console.log('[Supabase Auth] Initiating Discord OAuth flow...');

  // Get the current URL for redirect
  // For web: Just use the base URL
  // For Discord Activity: Preserve frame_id/instance_id query params
  const redirectTo = window.location.origin + window.location.pathname + window.location.search;

  if (import.meta.env.DEV) console.log('[Supabase Auth] Redirect URL:', redirectTo);

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'discord',
    options: {
      redirectTo,
      scopes: 'identify email guilds', // Include email for web users
      skipBrowserRedirect: false,
    },
  });

  if (error) {
    console.error('[Supabase Auth] Discord OAuth error:', error);
    throw new Error(`Failed to initiate Discord authentication: ${error.message}`);
  }

  // Browser will redirect to Discord, then back to our app
  if (import.meta.env.DEV) console.log('[Supabase Auth] Redirecting to Discord...');
}

/**
 * Fetch or create app user profile
 * Supports both Discord and Google OAuth providers
 * Exported for use in auth state change listeners
 */
export async function fetchOrCreateAppUser(authUser: User): Promise<AppUser> {
  if (import.meta.env.DEV)
    console.log('[Supabase Auth] Fetching app user for auth ID:', authUser.id);

  // Determine the provider from user metadata
  // Check app_metadata.provider first (set by Supabase), fallback to checking iss
  const appProvider = authUser.app_metadata?.provider as string | undefined;
  const isGoogleIss = authUser.user_metadata?.iss?.includes('google');
  const provider = appProvider || (isGoogleIss ? 'google' : 'discord');

  if (import.meta.env.DEV) console.log('[Supabase Auth] Provider detected:', provider);

  // Extract provider-specific data
  let discordId: string | null = null;
  let googleId: string | null = null;
  const email: string | null = authUser.email || null;

  if (provider === 'discord') {
    discordId = authUser.user_metadata?.provider_id || authUser.user_metadata?.sub;

    if (!discordId) {
      console.error('[Supabase Auth] Missing Discord ID in user metadata:', authUser.user_metadata);
      throw new Error('Discord authentication failed: missing Discord user ID');
    }
  } else if (provider === 'google') {
    googleId = authUser.user_metadata?.sub || authUser.user_metadata?.provider_id;

    if (!googleId) {
      console.error('[Supabase Auth] Missing Google ID in user metadata:', authUser.user_metadata);
      throw new Error('Google authentication failed: missing Google user ID');
    }
  }

  const username =
    authUser.user_metadata?.full_name ||
    authUser.user_metadata?.name ||
    authUser.user_metadata?.user_name ||
    authUser.user_metadata?.username ||
    authUser.email?.split('@')[0] ||
    `User ${authUser.id.substring(0, 8)}`;

  const avatar = authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || null;

  if (import.meta.env.DEV)
    console.log('[Supabase Auth] User data:', {
      provider,
      discordId,
      googleId,
      email,
      username,
      avatar,
    });

  // Try to fetch existing user by auth_user_id first
  const { data: existingUser, error: fetchError } = await supabase
    .from('users')
    .select('*')
    .eq('auth_user_id', authUser.id)
    .maybeSingle();

  if (fetchError) {
    console.error('[Supabase Auth] Error fetching user:', fetchError);
    throw new Error('Failed to fetch user profile');
  }

  // If user exists, update and return
  if (existingUser) {
    if (import.meta.env.DEV)
      console.log('[Supabase Auth] Found existing user:', existingUser.username);

    // Update user data based on provider
    if (provider === 'discord' && discordId) {
      const { data: updatedUser, error: updateError } = await supabase.rpc(
        'sync_discord_user_data',
        {
          p_auth_user_id: authUser.id,
          p_discord_id: discordId,
          p_username: username,
          p_avatar: avatar,
        }
      );

      if (updateError) {
        console.error('[Supabase Auth] Error updating user:', updateError);
        return existingUser as AppUser;
      }

      return updatedUser as AppUser;
    } else {
      // For Google users, just update last login and google_id/email
      const { error: updateError } = await supabase
        .from('users')
        .update({
          google_id: googleId,
          email: email,
          avatar: avatar || existingUser.avatar,
          last_login: new Date().toISOString(),
        })
        .eq('auth_user_id', authUser.id);

      if (updateError) {
        console.error('[Supabase Auth] Error updating Google user:', updateError);
      }

      // Fetch updated user
      const { data: refreshedUser } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();

      return (refreshedUser || existingUser) as AppUser;
    }
  }

  // User doesn't exist by auth_user_id - check for existing account to link
  if (import.meta.env.DEV)
    console.log('[Supabase Auth] No user found by auth_user_id, checking for existing account...');

  // For Discord users: check for legacy account
  if (provider === 'discord' && discordId) {
    const { data: backfilled, error: backfillError } = await supabase.rpc('backfill_auth_user_id', {
      p_discord_id: discordId,
      p_auth_user_id: authUser.id,
    });

    if (backfillError) {
      console.error('[Supabase Auth] Backfill RPC error:', backfillError);
      throw new Error(`Failed to backfill legacy account: ${backfillError.message}`);
    }

    if (backfilled === true) {
      if (import.meta.env.DEV)
        console.log('[Supabase Auth] Successfully linked legacy Discord account');

      const { data: linkedUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();

      if (fetchError || !linkedUser) {
        console.error('[Supabase Auth] Error fetching linked user:', fetchError);
        throw new Error('Failed to fetch linked user profile');
      }

      return linkedUser as AppUser;
    }
  }

  // For Google users: check if user exists by email (auto-link)
  if (provider === 'google' && email) {
    const { data: emailUser, error: emailError } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .maybeSingle();

    if (!emailError && emailUser) {
      if (import.meta.env.DEV)
        console.log('[Supabase Auth] Found existing user by email, linking Google account');

      // Link this Google identity to existing user
      const { error: linkError } = await supabase
        .from('users')
        .update({
          auth_user_id: authUser.id,
          google_id: googleId,
          last_login: new Date().toISOString(),
        })
        .eq('id', emailUser.id);

      if (linkError) {
        console.error('[Supabase Auth] Error linking Google to existing user:', linkError);
        throw new Error('Failed to link Google account');
      }

      return { ...emailUser, auth_user_id: authUser.id, google_id: googleId } as AppUser;
    }
  }

  // No existing user found - create new profile
  if (import.meta.env.DEV) console.log('[Supabase Auth] Creating new user profile...');

  const newUserData: Record<string, unknown> = {
    auth_user_id: authUser.id,
    username,
    avatar,
    email,
    last_login: new Date().toISOString(),
  };

  if (discordId) newUserData.discord_id = discordId;
  if (googleId) newUserData.google_id = googleId;

  const { data: newUser, error: createError } = await supabase
    .from('users')
    .insert(newUserData)
    .select()
    .single();

  if (createError) {
    // Handle race condition: User created by another request between our check and insert
    if (createError.code === '23505') {
      // Postgres unique_violation code
      if (import.meta.env.DEV)
        console.log('[Supabase Auth] Race condition detected, fetching existing user...');
      return (await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single()
        .then((r) => r.data)) as AppUser;
    }

    console.error('[Supabase Auth] Error creating user:', createError);
    throw new Error('Failed to create user profile');
  }

  if (import.meta.env.DEV) console.log('[Supabase Auth] Created new user:', newUser.username);
  return newUser as AppUser;
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  if (import.meta.env.DEV) console.log('[Supabase Auth] Signing out...');

  const { error } = await supabase.auth.signOut();

  if (error) {
    console.error('[Supabase Auth] Error signing out:', error);
    throw new Error('Failed to sign out');
  }

  if (import.meta.env.DEV) console.log('[Supabase Auth] Signed out successfully');
}

/**
 * Get current session
 */
export async function getCurrentSession(): Promise<Session | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session;
}

/**
 * Listen for auth state changes
 */
export function onAuthStateChange(callback: (session: Session | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
}

/**
 * Get Discord avatar URL
 */
export function getAvatarUrl(user: AppUser, size: number = 128): string {
  if (!user.avatar) {
    // Default Discord avatar (Discord has 6 default avatars: 0-5)
    const discriminator = parseInt(user.discord_id) % 6;
    return `https://cdn.discordapp.com/embed/avatars/${discriminator}.png`;
  }

  // Check if avatar is already a full URL
  if (user.avatar.startsWith('http')) {
    return user.avatar;
  }

  // Construct Discord CDN URL
  const format = user.avatar.startsWith('a_') ? 'gif' : 'png';
  return `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.${format}?size=${size}`;
}
